import React from 'react';
import { cn } from '../../utils/cn';

export interface FormSectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'danger';
  icon?: React.ReactNode;
}

/**
 * Form section component for organizing form content
 * Provides consistent spacing and visual hierarchy
 */
export const FormSection: React.FC<FormSectionProps> = ({
  title,
  description,
  children,
  className,
  variant = 'default',
  icon
}) => {
  const variantClasses = {
    default: 'border-border',
    danger: 'border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-950/10'
  };

  return (
    <div className={cn(
      'space-y-3 sm:space-y-4 p-3 sm:p-4 border rounded-lg',
      variantClasses[variant],
      className
    )}>
      {/* Section Header */}
      {(title || description) && (
        <div className="space-y-1 sm:space-y-2">
          {title && (
            <h3 className={cn(
              'text-sm sm:text-base font-medium flex items-center gap-2',
              variant === 'danger' ? 'text-red-900 dark:text-red-100' : 'text-foreground'
            )}>
              {icon}
              {title}
            </h3>
          )}
          {description && (
            <p className={cn(
              'text-xs sm:text-sm leading-relaxed',
              variant === 'danger' 
                ? 'text-red-700 dark:text-red-300' 
                : 'text-muted-foreground'
            )}>
              {description}
            </p>
          )}
        </div>
      )}

      {/* Section Content */}
      <div className="space-y-3 sm:space-y-4">
        {children}
      </div>
    </div>
  );
};